import { useCallback } from 'react'
import { toast } from '@sales-center/components'
import { useAppDispatch } from '@user/store/hooks'
import { AddNoteFormModel } from '@user/components/Notes/AddNoteDrawer/typings'
import { validateNote } from '@user/components/Notes/EditNoteDrawer/utils/validateNote'
import { updateOpportunityNote } from '@user/store/opportunityNotes/api'
import { updateLeadNote } from '@user/store/leadNotes/api'
import { CreateLeadNoteInput } from '@user/store/leadNotes/typings'
import { CreateOpportunityNoteInput } from '@user/store/opportunityNotes/typings'
import { LocationDTO } from '@sales-center/common'

type Props = {
  entityId: number
  noteId: number
  onUpdateSuccess: () => void
  onGoBack?: () => void
  serviceOptions: LocationDTO[]
}

export function useHandlers(props: Props) {
  const { noteId, entityId, onUpdateSuccess, serviceOptions, onGoBack } = props

  const dispatch = useAppDispatch()

  const onSubmitLeadNote = useCallback(
    async (model: AddNoteFormModel) => {
      const { detail, title, addNotesToPestPac } = model
      let data: CreateLeadNoteInput = {
        detail,
        id: noteId,
        leadId: entityId,
        title: title || '',
      }

      if (addNotesToPestPac) {
        data = {
          ...data,
          ...getNoteUpdateObject({ data, model, serviceOptions }),
        }
      }
      const response = await dispatch(updateLeadNote(data))

      if (updateLeadNote.fulfilled.match(response)) {
        toast.success(
          addNotesToPestPac ? `Note added to location(s)` : `Note updated.`,
        )
        onGoBack ? onGoBack() : onUpdateSuccess()
      } else {
        toast.error(`Failed to update note.`)
      }
    },
    [onGoBack, dispatch, entityId, noteId, onUpdateSuccess, serviceOptions],
  )

  const onSubmitOppNote = useCallback(
    async (model: AddNoteFormModel) => {
      const { detail, title, addNotesToPestPac } = model
      let data: CreateOpportunityNoteInput = {
        detail,
        id: noteId,
        opportunityId: entityId,
        title: title || '',
      }

      if (addNotesToPestPac) {
        data = {
          ...data,
          ...getNoteUpdateObject({ data, model, serviceOptions }),
        }
      }

      const response = await dispatch(updateOpportunityNote(data))

      if (updateOpportunityNote.fulfilled.match(response)) {
        toast.success(
          addNotesToPestPac ? `Note added to location(s)` : `Note updated.`,
        )
        onGoBack ? onGoBack() : onUpdateSuccess()
      } else {
        toast.error(`Failed to update note.`)
      }
    },
    [onGoBack, dispatch, noteId, onUpdateSuccess, entityId, serviceOptions],
  )

  const validate = useCallback((model: AddNoteFormModel) => {
    return { ...validateNote(model) }
  }, [])

  return {
    onSubmitLeadNote,
    onSubmitOppNote,
    validate,
  }
}

interface getNoteUpdateObjectProps {
  data: CreateOpportunityNoteInput | CreateLeadNoteInput
  model: AddNoteFormModel
  serviceOptions: LocationDTO[]
}

function getNoteUpdateObject(props: getNoteUpdateObjectProps) {
  const { data, model, serviceOptions } = props
  const { code, serviceLocation } = model
  data.locationId =
    serviceLocation &&
    serviceOptions.filter(
      (location) => location.id === Number(serviceLocation),
    )[0]?.externalLinkId
  data.noteCode = code
  data.locationCode =
    serviceLocation &&
    serviceOptions.filter(
      (location) => location.id === Number(serviceLocation),
    )[0]?.locationCode

  return data
}
